/** Decode STM32 APP_FaultType_t — khớp SoftDebug / server faultCodes.js */

export const FAULT_DEFINITIONS = [
  { bit: 0, code: 'OC_TRIP', title: 'Quá dòng HERIC (trip)', severity: 'error', softdebug: 'F001' },
  { bit: 1, code: 'OC_LATCH', title: 'Quá dòng HERIC (khóa cứng)', severity: 'critical', softdebug: 'F002' },
  { bit: 2, code: 'DAB_ILCK_LV', title: 'DAB interlock LV thấp', severity: 'error', softdebug: 'F003' },
  { bit: 3, code: 'DAB_ILCK_HV', title: 'DAB interlock HV (reserved)', severity: 'warning', softdebug: 'F004' },
  { bit: 4, code: 'DAB_START_TIMEOUT', title: 'DAB không lên bus HV', severity: 'critical', softdebug: 'F005' },
  { bit: 5, code: 'SPWM_HV_LOW', title: 'Bus HV thấp — HERIC dừng', severity: 'warning', softdebug: 'F006' },
  { bit: 6, code: 'DAB_WAIT_PRECHARGE', title: 'Đang chờ precharge DAB', severity: 'info', softdebug: 'W001' },
  { bit: 7, code: 'OC_DAB_TRIP', title: 'Quá dòng DAB HV (trip)', severity: 'error', softdebug: 'F007' },
  { bit: 8, code: 'OC_DAB_LATCH', title: 'Quá dòng DAB HV (khóa cứng)', severity: 'critical', softdebug: 'F008' },
  { bit: 9, code: 'OC_DAB_INRUSH', title: 'Inrush DAB cao', severity: 'warning', softdebug: 'W002' },
  { bit: 10, code: 'SPWM_HV_HIGH', title: 'Bus HV quá cao — cắt HERIC', severity: 'error', softdebug: 'F011' },
  { bit: 11, code: 'PLL_FREQ', title: 'PLL tần số lưới lệch', severity: 'error', softdebug: 'F012' },
  { bit: 12, code: 'PLL_VAC', title: 'PLL điện áp lưới lệch', severity: 'error', softdebug: 'F013' },
];

export function decodeFaultMask(mask) {
  const value = Number(mask);
  if (!Number.isFinite(value) || value <= 0) {
    return [];
  }
  return FAULT_DEFINITIONS.filter((def) => (value & (1 << def.bit)) !== 0);
}

export function invStatusFlags(invStatus) {
  const s = Number(invStatus) || 0;
  return {
    running: Boolean(s & (1 << 0)),
    phaseOk: Boolean(s & (1 << 1)),
    pllOk: Boolean(s & (1 << 2)),
    hericCmd: Boolean(s & (1 << 3)),
    relayHeric: Boolean(s & (1 << 4)),
    relayAc: Boolean(s & (1 << 5)),
    vacMatched: Boolean(s & (1 << 6)),
  };
}

export const SYSTEM_MODE_LABEL = {
  0: 'Off-grid',
  1: 'On-grid',
};

export const INV_GRID_STATE_LABEL = {
  0: 'IDLE',
  1: 'WAIT_PLL',
  2: 'SYNC',
  3: 'RUN',
  4: 'FAULT',
};

export const POWER_ARBITER_LABEL = {
  0: 'BOOT',
  1: 'STANDBY',
  2: 'PRECHARGE',
  3: 'DAB_RUN',
  4: 'INV_RUN',
  5: 'FAULT_LATCH',
};

/**
 * Map SoftDebug page fields ← MQTT currentData (chuẩn hoá).
 * SoftDebug UART BOARD/DAB → cùng semantic với payload MQTTS.
 */
export function mapSoftDebugFromMqtt(currentData = {}, inverter = {}) {
  const d = currentData || {};
  const adc = d.adcRaw || {};
  const flags = invStatusFlags(d.invStatus);
  const faults = decodeFaultMask(d.faultMask);
  const warnings = faults.filter((f) => f.severity === 'warning' || f.severity === 'info');
  const hardFaults = faults.filter((f) => f.severity === 'error' || f.severity === 'critical');

  const hvV = d.dabHvVoltage ?? d.hvBusVoltage;
  const hvI = d.dabHvCurrentA;
  const lvV = d.dabLvVoltage ?? d.batteryVoltage;
  const hvP = d.dabHvPower ?? (
    hvV != null && hvI != null ? Number(hvV) * Number(hvI) : null
  );
  /* SoftDebug: I_LV ≈ (V_HV × I_HV) / V_LV khi có đủ số. */
  let lvI = null;
  if (hvV != null && hvI != null && lvV != null && Number(lvV) !== 0) {
    lvI = (Number(hvV) * Number(hvI)) / Number(lvV);
  }
  const lvP = lvV != null && lvI != null ? Number(lvV) * Number(lvI) : null;

  return {
    meta: {
      name: inverter.name || d.name || '—',
      deviceId: inverter.deviceId || d.serialNumber || '—',
      model: inverter.model || d.model || '—',
      stm32Link: Boolean(d.stm32Link),
      debugMode: Boolean(d.debugMode),
      status: d.status || inverter.status || '—',
      updatedAt: d.updatedAt || inverter.updated_at || null,
      wifiRssi: d.wifiRssi,
      espTemperature: d.espTemperature,
      mcuTickMs: d.mcuTickMs,
      connection: inverter.connection || {},
    },
    dashboard: {
      systemMode: d.systemMode,
      systemModeLabel: SYSTEM_MODE_LABEL[d.systemMode] || `mode ${d.systemMode ?? '—'}`,
      dabEnabled: d.dabEnabled ?? Boolean(d.dabCmdEn),
      dabAllowed: Boolean(d.dabAllowed),
      pvPower: d.pvPower,
      batteryPower: d.batteryPower,
      gridPower: d.gridPower,
      loadPower: d.loadPower,
      dabPower: d.dabHvPower,
      pvVoltage: d.pvVoltage,
      batteryVoltage: d.batteryVoltage,
      hvBusVoltage: d.hvBusVoltage ?? d.dabHvVoltage,
      gridVoltage: d.gridVoltage,
      gridFrequency: d.gridFrequency,
      warnings,
      hardFaults,
    },
    realtime: {
      pvVoltage: d.pvVoltage,
      pvCurrent: d.pvCurrent,
      batteryVoltage: d.batteryVoltage,
      hvBusVoltage: d.hvBusVoltage ?? d.dabHvVoltage,
      gridVoltage: d.gridVoltage,
      gridCurrent: d.gridCurrent,
      adc,
      pvPower: d.pvPower,
      batteryPower: d.batteryPower,
      gridPower: d.gridPower,
      loadPower: d.loadPower,
      gridFrequency: d.gridFrequency,
      gridFrequencyZc: d.gridFrequencyZc,
    },
    faults: {
      mask: d.faultMask ?? 0,
      value: d.faultValue,
      lastId: d.faultLastId,
      active: faults,
    },
    dab: {
      enabled: d.dabEnabled ?? Boolean(d.dabCmdEn),
      allowed: Boolean(d.dabAllowed),
      mode: d.dabMode,
      modeLabel: Number(d.dabMode) === 1 ? 'Closed-loop' : 'Open-loop',
      phaseDeg: d.dabPhaseDeg,
      lvVoltage: lvV,
      lvCurrentRaw: d.dabLvCurrentRaw,
      lvCurrentA: lvI,
      lvPower: lvP,
      hvVoltage: hvV,
      hvCurrentA: hvI,
      hvCurrentRaw: d.dabHvCurrentRaw,
      hvPower: hvP,
      hvTargetV: d.dabHvTargetV,
      vErrV: d.dabVErrV,
      targetCmdV: d.dabTargetCmdV,
      piIntegral: d.dabPiIntegral,
      direction:
        d.dabHvCurrentA == null
          ? '—'
          : Number(d.dabHvCurrentA) >= 0
            ? 'Sạc (HV←LV)'
            : 'Xả (HV→LV)',
    },
    inverter: {
      vacRms: d.gridVoltage,
      iacRms: d.gridCurrent,
      hericFrequency: d.hericFrequency,
      acActivePower: d.acActivePower ?? d.gridPower,
      acCosPhi: d.acCosPhi,
      acApparentPower: d.acApparentPower,
      gridFreqZc: d.gridFrequencyZc,
      gridFreqPll: d.gridFrequency,
      pllVd: d.pllVd,
      pllVq: d.pllVq,
      invGridState: d.invGridState,
      invGridLabel: INV_GRID_STATE_LABEL[d.invGridState] || String(d.invGridState ?? '—'),
      powerArbiter: d.powerArbiterState,
      powerArbiterLabel: POWER_ARBITER_LABEL[d.powerArbiterState] || String(d.powerArbiterState ?? '—'),
      flags,
      systemMode: d.systemMode,
      systemModeLabel: SYSTEM_MODE_LABEL[d.systemMode] || '—',
      hvBusVoltage: d.hvBusVoltage ?? d.dabHvVoltage,
      pvVoltage: d.pvVoltage,
      pvCurrent: d.pvCurrent,
      pvPower: d.pvPower,
      batteryVoltage: d.batteryVoltage,
    },
    mppt: {
      pvVoltage: d.pvVoltage,
      pvCurrent: d.pvCurrent,
      pvPower: d.pvPower,
      hvBusVoltage: d.hvBusVoltage ?? d.dabHvVoltage,
    },
    pll: {
      freqHz: d.gridFrequency,
      freqZcHz: d.gridFrequencyZc,
      vd: d.pllVd,
      vq: d.pllVq,
      drop: d.pllDrop,
      pending: d.pllPending,
      flags,
    },
  };
}

export function fmt(value, digits = 2, unit = '') {
  if (value == null || Number.isNaN(Number(value))) {
    return '—';
  }
  const n = Number(value);
  const text = Number.isInteger(n) && digits === 0 ? String(n) : n.toFixed(digits);
  return unit ? `${text} ${unit}` : text;
}
